export type TransportIntervalsCartMode =
  | 'per_product_or_percentage'
  | 'always_sum'
  | 'single_parcel'

export type TransportIntervalsQuantityMode = 'times_qty' | 'once' | 'weight_times_qty'

export interface TransportIntervalBand {
  minKg: number
  maxKg: number
  minLengthCm?: number | null
  maxLengthCm?: number | null
  minWidthCm?: number | null
  maxWidthCm?: number | null
  minHeightCm?: number | null
  maxHeightCm?: number | null
  priceTur: number
  priceRetur: number
}

export interface TransportIntervalsConfig {
  enabled: boolean
  cartMode: TransportIntervalsCartMode
  quantityMode: TransportIntervalsQuantityMode
  multiProductMode: 'per_product' | 'percentage'
  percentage: number
  intervals: TransportIntervalBand[]
}

export interface TransportIntervalsLine {
  productId: number
  quantity: number
  weightKgTur?: number | null
  weightKgRetur?: number | null
}

function roundMoney(value: number): number {
  return Math.round((Number(value) || 0) * 100) / 100
}

function positiveMoney(value: unknown): number {
  const n = Number(value)
  if (!Number.isFinite(n) || n <= 0) return 0
  return roundMoney(n)
}

function positiveKg(value: unknown): number {
  const n = Number(value)
  if (!Number.isFinite(n) || n < 0) return 0
  return n
}

export function priceForIntervalKg(
  intervals: TransportIntervalBand[],
  kg: number,
  kind: 'tur' | 'retur',
): number {
  if (!intervals.length) return 0
  const weight = positiveKg(kg)
  if (weight <= 0) return 0
  const match = intervals.find((row) => {
    const min = Number(row.minKg)
    const max = Number(row.maxKg)
    const from = Number.isFinite(min) ? min : 0
    const to = Number.isFinite(max) ? max : from
    return weight >= from && weight <= to
  })
  const row = match ?? intervals[intervals.length - 1]
  return kind === 'tur' ? positiveMoney(row.priceTur) : positiveMoney(row.priceRetur)
}

function lineQty(line: TransportIntervalsLine): number {
  return Math.max(1, Math.floor(Number(line.quantity) || 1))
}

function lineShipping(
  line: TransportIntervalsLine,
  intervals: TransportIntervalBand[],
  quantityMode: TransportIntervalsQuantityMode,
): number {
  const qty = lineQty(line)
  const kgTur = positiveKg(line.weightKgTur)
  const kgRetur = positiveKg(line.weightKgRetur)
  if (quantityMode === 'once') {
    return roundMoney(
      priceForIntervalKg(intervals, kgTur, 'tur') + priceForIntervalKg(intervals, kgRetur, 'retur'),
    )
  }
  if (quantityMode === 'weight_times_qty') {
    return roundMoney(
      priceForIntervalKg(intervals, kgTur * qty, 'tur')
      + priceForIntervalKg(intervals, kgRetur * qty, 'retur'),
    )
  }
  return roundMoney(
    (priceForIntervalKg(intervals, kgTur, 'tur') + priceForIntervalKg(intervals, kgRetur, 'retur')) * qty,
  )
}

export function calculateTransportIntervalsShipping(
  lines: TransportIntervalsLine[],
  config: Pick<
    TransportIntervalsConfig,
    'enabled' | 'cartMode' | 'quantityMode' | 'multiProductMode' | 'percentage' | 'intervals'
  >,
  eligibleSubtotal = 0,
): number {
  if (!config.enabled || !lines.length) return 0
  const intervals = Array.isArray(config.intervals) ? config.intervals : []
  if (!intervals.length) return 0

  if (config.cartMode === 'single_parcel') {
    const useQty = config.quantityMode !== 'once'
    let totalTur = 0
    let totalRetur = 0
    for (const line of lines) {
      const qty = useQty ? lineQty(line) : 1
      totalTur += positiveKg(line.weightKgTur) * qty
      totalRetur += positiveKg(line.weightKgRetur) * qty
    }
    return roundMoney(
      priceForIntervalKg(intervals, totalTur, 'tur')
      + priceForIntervalKg(intervals, totalRetur, 'retur'),
    )
  }

  const uniqueProductCount = new Set(
    lines.map((line) => Number(line.productId)).filter((id) => Number.isFinite(id) && id > 0),
  ).size

  if (
    config.cartMode === 'per_product_or_percentage'
    && config.multiProductMode === 'percentage'
    && uniqueProductCount > 1
  ) {
    const pct = Math.max(0, Number(config.percentage) || 0)
    return roundMoney((Math.max(0, Number(eligibleSubtotal) || 0) * pct) / 100)
  }

  return roundMoney(
    lines.reduce((sum, line) => sum + lineShipping(line, intervals, config.quantityMode), 0),
  )
}

export function emptyIntervalBand(): TransportIntervalBand {
  return {
    minKg: 0,
    maxKg: 5,
    minLengthCm: 0,
    maxLengthCm: 0,
    minWidthCm: 0,
    maxWidthCm: 0,
    minHeightCm: 0,
    maxHeightCm: 0,
    priceTur: 0,
    priceRetur: 0,
  }
}
